import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { TEXT_INPUT_TYPES } from '../utils/projectScopeForm';

const ProjectScopeFieldQuestion = ({ field, value, onChange, onSubmitOnEnter, onAutoAdvance, error }) => {
  const choices = field.properties?.choices || [];
  const isMulti = Boolean(field.properties?.allow_multiple_selection);

  const toggleChoice = (ref) => {
    if (isMulti) {
      const current = Array.isArray(value) ? value : [];
      onChange(current.includes(ref) ? current.filter((r) => r !== ref) : [...current, ref]);
    } else {
      onChange(ref);
      if (onAutoAdvance) setTimeout(onAutoAdvance, 200);
    }
  };

  return (
    <div>
      <h4 className="text-xl font-semibold text-white mb-1 flex items-center">
        <CheckCircle2 className="w-5 h-5 text-orange-400 mr-2 flex-shrink-0" />
        {field.title || 'Your answer'}
      </h4>
      {field.required && <p className="text-gray-500 text-xs mb-4 ml-7">Required</p>}

      <div className="mt-4">
        {(field.type === 'dropdown' || field.type === 'multiple_choice') && (
          <div className="space-y-2">
            {choices.map((choice) => {
              const selected = isMulti ? (value || []).includes(choice.ref) : value === choice.ref;
              return (
                <button
                  key={choice.ref}
                  type="button"
                  onClick={() => toggleChoice(choice.ref)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    selected ? 'bg-orange-600/20 border-orange-500 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {choice.label}
                </button>
              );
            })}
          </div>
        )}

        {field.type === 'yes_no' && (
          <div className="flex gap-3">
            {[{ label: 'Yes', val: true }, { label: 'No', val: false }].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => { onChange(opt.val); if (onAutoAdvance) setTimeout(onAutoAdvance, 200); }}
                className={`flex-1 px-4 py-3 rounded-lg border transition-colors ${
                  value === opt.val ? 'bg-orange-600/20 border-orange-500 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {field.type === 'long_text' && (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors resize-none"
          />
        )}

        {TEXT_INPUT_TYPES[field.type] && (
          <input
            type={TEXT_INPUT_TYPES[field.type]}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSubmitOnEnter()}
            className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors ${error ? 'border-red-500 focus:border-red-400' : 'border-white/10 focus:border-orange-400'}`}
          />
        )}
        {error && (
          <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
            <span>⚠</span> {error}
          </p>
        )}

        {field.type === 'statement' && <p className="text-gray-400 text-sm">Next up: a few questions specific to your industry.</p>}

        {!['dropdown','multiple_choice','yes_no','long_text','statement'].includes(field.type) && !TEXT_INPUT_TYPES[field.type] && (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSubmitOnEnter()}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
          />
        )}
      </div>
    </div>
  );
};

export default ProjectScopeFieldQuestion;
